import React, { useState, useEffect, Component } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import { API } from '../api';
import i18n from '../i18n'; // para SeccionBoundary (clase, sin hooks)
import { ArrowLeft, LogOut } from 'lucide-react';
import './menuInicio.css';
import ClientDashboard from './ClientDashboard';
import ReservaServicioModal from '../components/client/ReservaServicioModal';
import MiPerfil from './MiPerfil';
import EspacioPacienteClinica from './EspacioPacienteClinica';
import VisionClienteTab from '../components/admin/canal/VisionClienteTab';
import TutorialBienvenida from '../components/client/TutorialBienvenida'; // tutorial REAL (tour sobre el dashboard)
import SelectorIdioma from '../components/SelectorIdioma';
import { track } from '../utils/track'; // rastreador de comportamiento
import ConsentimientoGDPRModal from '../components/ConsentimientoGDPRModal';
import BlindajeCanal from '../components/client/BlindajeCanal'; // aviso legal + registro ante intentos de descarga (Ann 2026-07-16)

// ── Blindaje Guardián 2026-07-13 ──────────────────────────────────────────
// Una sección que reviente al renderizar (p.ej. el reproductor tras restaurar
// la sección guardada en localStorage) NUNCA debe dejar la plataforma en
// blanco. Este límite de error captura el fallo, limpia la sección persistida
// y ofrece volver al menú. Antes: pantalla en blanco = usuario Premium fuera.
class SeccionBoundary extends Component {
  constructor(props) { super(props); this.state = { error: false }; }
  static getDerivedStateFromError() { return { error: true }; }
  componentDidCatch(err) {
    try { localStorage.removeItem('canal_seccion'); } catch (e) { /* noop */ }
    try { console.error('[canal] la sección falló al abrir; vuelvo al menú:', err); } catch (e) { /* noop */ }
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, textAlign: 'center', background: '#e9f0ed' }}>
          <p style={{ color: '#253634', fontWeight: 600, maxWidth: 360, lineHeight: 1.5 }}>{i18n.t('menu:error.noSeccion')}</p>
          <button onClick={() => { this.setState({ error: false }); if (this.props.onReset) this.props.onReset(); }}
            style={{ background: 'linear-gradient(135deg,#3C8696,#2C6675)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 28px', fontWeight: 700, cursor: 'pointer' }}>
            {i18n.t('menu:error.volverMenu')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const CanalPilarDashboardInner = ({ user, onLogout, onBackToSelector }) => {
  // i18n (zona 3 bilingüe ES/CA): todos los textos del menú salen del namespace `menu`.
  const { t } = useTranslation('menu');
  // PROVISIONAL (demo inicial): abre el asistente de bienvenida. Quitar tras la demo.
  const [showDemoBienvenida, setShowDemoBienvenida] = useState(false);
  // Sección abierta PERSISTIDA: al refrescar (F5) no se pierde ni expulsa al
  // usuario al menú. Se guarda en localStorage y se restaura al montar; al
  // volver al menú (null) se limpia.
  const _SECCIONES_VALIDAS = ['clases', 'canal', 'perfil', 'clinica'];
  const [activeSection, _setActiveSection] = useState(() => {
    try { const s = localStorage.getItem('canal_seccion'); return _SECCIONES_VALIDAS.includes(s) ? s : null; } catch (e) { return null; }
  });
  const setActiveSection = (v) => {
    _setActiveSection(v);
    // Persistir la sección abierta (fix Ann 2026-07-21): al refrescar (F5) NO se
    // pierde ni expulsa al usuario al menú. Se restaura al montar (ver useState de
    // arriba). Al volver al menú (null) se limpia. Faltaba este setItem/removeItem.
    try {
      if (v) localStorage.setItem('canal_seccion', v);
      else localStorage.removeItem('canal_seccion');
    } catch (e) { /* noop */ }
    if (v) setSaludar(false); // al navegar a una sección, deja de saludar
    // Rastreador de navegación (Ann 2026-07-14): registra en qué sección ENTRA
    // el cliente, aunque no vea ningún vídeo. 'canal'=vídeos, 'clases'=gestor de
    // cambios, 'perfil', 'clinica'. Fire-and-forget, no bloquea.
    if (v) { try { track('seccion_vista', v); } catch (e) { /* noop */ } }
  };
  const [tieneFisio, setTieneFisio] = useState(false);
  // Cliente SOLO-CANAL (Club/online: sin clases fijas y sin fisio) → al llegar
  // se le abre DIRECTAMENTE el canal (Ann 2026-07-15): el menú no le aporta
  // nada al aterrizar (su única sección de contenido es el canal). «Tornar»
  // sigue llevando al menú (ahí tiene Mi Perfil). Premium/fisio → menú, como hasta ahora.
  const [soloCanal, setSoloCanal] = useState(false);
  // Horarios REALES de clases fijas (Muntaner/MEFIR) para el bloque del menú (Ann 2026-07-15, propuesta B).
  const [horariosSedes, setHorariosSedes] = useState([]);
  const [fotoCliente, setFotoCliente] = useState(null);   // foto del cliente para el saludo
  // FASE 2 (fisio): abre el modal de reserva. El componente real
  // (ReservaServicioModal) lo entrega el agente compras-canal-video; hasta
  // entonces, placeholder "muy pronto". Enganche listo para sustituir.
  const [showReservaFisio, setShowReservaFisio] = useState(false);

  // Re-firma de normativa por MODELO actualizado (botón "Modelo actualizado a
  // fecha de hoy" en Textos Legales). Si el backend dice que debe re-firmar,
  // se BLOQUEA toda la plataforma del cliente hasta que vuelva a firmar.
  const [debeRefirmar, setDebeRefirmar] = useState(false);
  // Carga unificada (fix "el menú se pinta a saltos", Auditoría): no se pinta el
  // menú hasta que TODOS los fetch de layout resuelven (ver efecto único abajo).
  const [cargando, setCargando] = useState(true);
  // (estado-consentimiento movido al efecto unificado de abajo)

  // "¡Opinar!" → reseñas de Google (misma URL que la barra ClientTopBar).
  const REVIEW_URL = 'https://g.page/r/CRWuzRPOXq60EAE/review';

  // Mensaje de llegada. NORMA (Ann): "solo se dice lo que es VERDAD".
  // Cumpleaños / aniversario / días clave / asistencia vendrán del BACKEND DE VERACIDAD
  // (GET /cliente/mensaje-llegada, PENDIENTE · docs/TAREAS_PENDIENTES_CANAL_VIDEO.md).
  // Hasta que exista, se muestra SOLO el fallback motivador genérico (siempre cierto, nunca vacío).
  // i18n: las frases viven en menu.json (motivacionales). Se persiste el ÍNDICE
  // aleatorio, no el texto → al cambiar de idioma la frase cambia con él (sin fugas).
  const MOTIVACIONALES = t('motivacionales', { returnObjects: true });
  const [mensajeIdx] = useState(() => Math.floor(Math.random() * 30));
  const [mensajeBackend, setMensajeBackend] = useState(null); // texto del backend de veracidad (si llega)
  const mensajeLlegada = {
    tx: mensajeBackend?.texto
      || (Array.isArray(MOTIVACIONALES) ? MOTIVACIONALES[mensajeIdx % MOTIVACIONALES.length] : ''),
    cta: mensajeBackend?.cta || '',
    tipo: mensajeBackend?.tipo,
    dias: mensajeBackend?.dias_sin_entrar,
  };
  // Saludo "Hola" SOLO nada más llegar; al navegar a una sección se desactiva y ya no vuelve.
  const [saludar, setSaludar] = useState(() => {
    try { return !localStorage.getItem('canal_seccion'); } catch (e) { return true; }
  });
  const primerNombre = (user?.nombre || '').trim().split(/\s+/)[0] || '';

  // (foto-perfil, mensaje-llegada y tipo-acceso movidos al efecto unificado de abajo)

  // Marquesina de novedades. FUENTE ÚNICA: el editor de mensaje/novedad al
  // cliente de Controles (/client/anuncio-cliente). El MISMO texto sale también
  // en la pantalla "Novedades" del canal. Sin mensaje activo → no se muestra.
  // (Nada de demo ni de la clave muerta settings/avisos/novedades_cliente.)
  const [novedades, setNovedades] = useState([]);

  // ── CARGA UNIFICADA (fix Auditoría · "el menú se pinta a saltos") ──
  // Todos los fetch que definen el layout del menú (foto, tile Clínica, novedades,
  // mensaje de llegada, re-firma) van EN PARALELO; el menú no se pinta hasta que
  // TODOS resuelven (cargando=false). Antes cada uno tenía su useEffect y pintaba
  // su bloque por separado → aparición escalonada. Mismo patrón que CanalClienteView.
  useEffect(() => {
    Promise.allSettled([
      // PRIMERA firma o RE-firma: ambas bloquean con el mismo modal (return
      // temprano). Quién debe firmar lo decide el BACKEND (Ann 2026-07-13:
      // primera firma SOLO Premium/Mefir con la normativa visible; el Club
      // entra directo al canal sin firmar).
      axios.get(`${API}/cliente/estado-consentimiento`)
        .then(r => {
          if (r.data?.debe_refirmar_normativa || r.data?.debe_firmar_normativa_primera) setDebeRefirmar(true);
        }),
      axios.get(`${API}/cliente/foto-perfil-estado`)
        .then(r => setFotoCliente(r.data?.foto_perfil || null)),
      axios.get(`${API}/paciente/tipo-acceso`)
        .then(r => {
          const t = r.data?.tipo_acceso; setTieneFisio(t === 'ambos' || t === 'solo_paciente');
          // CLUB = lo dice su MEMBRESÍA (Ann 2026-07-20). Antes se deducía por
          // descarte («ni clases fijas ni fisio») y una socia Club que TAMBIÉN
          // hace fisio quedaba fuera → le salía la vista de Premium. Fallback al
          // criterio antiguo solo si el backend aún no manda `es_club`.
          setSoloCanal(r.data?.es_club === true
            || (r.data?.es_club === undefined
                && r.data?.tiene_clases_fijas !== true
                && r.data?.plataforma_paciente_activa !== true));
        }),
      axios.get(`${API}/client/anuncio-cliente`)
        .then(r => { const a = r.data; setNovedades(a && a.activo && a.texto ? [a.texto] : []); }),
      axios.get(`${API}/paciente/horarios-clases`)
        .then(r => setHorariosSedes(Array.isArray(r.data?.sedes) ? r.data.sedes : [])),
      axios.get(`${API}/cliente/mensaje-llegada`)
        .then(r => {
          // tipo (backend de veracidad, PENDIENTE): 'bienvenida' | 'regreso' | 'motivacional'.
          if (r.data?.texto || r.data?.tipo) setMensajeBackend(r.data);
        }),
    ]).finally(() => setCargando(false));
  }, []);

  // ── Tutorial de bienvenida en la PRIMERA entrada ──────────────────────────
  // Antes: OnboardingBienvenida solo se abría con el botón manual "Demo
  // bienvenida" → a un Premium nuevo NUNCA le saltaba. Ahora salta una vez y se
  // recuerda que ya se vio (localStorage). Si hay firma pendiente, el modal de
  // normativa se muestra primero (return temprano) y el tutorial aparece después.
  useEffect(() => {
    try {
      if (!localStorage.getItem('canal_bienvenida_vista')) {
        setShowDemoBienvenida(true);
        track('tutorial_inicio');  // rastreador de comportamiento (Ann 2026-07-14)
      }
    } catch (e) { /* noop */ }
  }, []);

  // ── Entrada directa al canal para el cliente SOLO-CANAL (Club/online) ──
  // Se decide UNA vez, al terminar la carga y solo AL LLEGAR (sin sección
  // persistida): si el cliente pulsa «Tornar» después, el menú se queda
  // (no se le vuelve a expulsar al canal). La re-firma bloquea después igualmente.
  useEffect(() => {
    if (debeRefirmar) return;   // con firma pendiente no se navega a ningún sitio
    if (cargando || !soloCanal) return;
    try { if (localStorage.getItem('canal_seccion')) return; } catch (e) { /* noop */ }
    if (!activeSection) setActiveSection('canal');
  }, [cargando, soloCanal, debeRefirmar]); // eslint-disable-line

  // ── Re-firma obligatoria: bloquea TODA la plataforma (canal online, gestor,
  //    perfil, clínica) hasta que el cliente vuelva a leer y firmar la normativa
  //    actualizada. El modal registra la nueva versión firmada al aceptar. ──
  //
  // 🚨 EL FALLO QUE ATRAPÓ A 442 CLIENTAS (Guardián + Ann, 16-jul-2026) ──────
  // Este `return` estaba ANTES del useEffect de aquí arriba. React exige que
  // TODOS los hooks se ejecuten SIEMPRE y en el mismo orden: `debeRefirmar`
  // empieza en false (se ejecutan todos los hooks) y una petición lo pone a
  // true → en el siguiente pintado el return temprano SALTABA por encima del
  // useEffect → React contaba menos hooks que en el pintado anterior y
  // REVENTABA con el error REAL capturado en local con Ann:
  //     "Rendered fewer hooks than expected. This may be caused by an
  //      accidental early return statement."   at CanalPilarDashboardInner
  //
  // Consecuencia: ENSEÑAR la normativa rompía la plataforma entera. La clienta
  // veía "No hemos podido abrir esta sección", pulsaba «Volver al menú», el
  // botón recargaba la página… y volvía a reventar. LIMBO. No podía firmar
  // NUNCA. Y sin firma no hay canal.
  //
  // MEDIDO en producción (16-jul): 442 de 534 clientas (82%) SIN FIRMAR, 266 de
  // ellas PAGANDO cuota activa. De las que firmaron ven vídeos el 32%; de las
  // que no firmaron, el 1%. El muro era total y nadie podía cruzarlo.
  //
  // ⚠️ REGLA PARA QUIEN VENGA DESPUÉS: en un componente con hooks, NINGÚN
  // `return` puede ir antes del último hook. Si hay que cortar el render, se
  // corta DESPUÉS de todos los useState/useEffect/useMemo.
  if (debeRefirmar) {
    return (
      <ConsentimientoGDPRModal
        isOpen={true}
        onAccept={() => setDebeRefirmar(false)}
        onReject={onLogout}
        userName={user?.nombre || 'Cliente'}
      />
    );
  }

  // ── Renderizado de secciones activas ──
  // Blindaje Guardián: cada sección se compone en una variable y se devuelve
  // envuelta en SeccionBoundary; si revienta, cae al menú en vez de dejar blanco.
  let _seccionEl = null;
  if (activeSection === 'clases') {
    _seccionEl = (
      <ClientDashboard
        user={user}
        onLogout={onLogout}
        onBackToSelector={() => setActiveSection(null)}
      />
    );
  } else if (activeSection === 'canal') {
    _seccionEl = (
      <div className="relative min-h-screen flex flex-col bg-white">
        {/* SOCIA SOLO-CANAL (Club / club_friend) · Ann 2026-07-20, urgente:
            «quita la barra verde y pon arriba a la derecha salir».
            La barra tenía «Volver», que la llevaba a un menú de secciones que NO
            son suyas (incumplía la norma de enseñar solo lo que se tiene). Para
            ella, el Canal ES su sitio: no hay a dónde volver, solo salir. */}
        {soloCanal ? (
          /* Sin barra: el botón FLOTA sobre la portada (Ann 2026-07-20:
             «no se puede poner el botón salir encima de la foto? sin que tenga
             que haber una barra?»). Así no roba altura al contenido.
             Solo «Salir»: su perfil lo tiene en «Mi Espacio». */
          <button onClick={onLogout} data-testid="canal-salir"
            className="absolute top-3 right-4 z-30 inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[13px] font-semibold text-white bg-[#077069] border border-white/40 shadow-md hover:bg-[#0a5f59]">
            <LogOut className="w-4 h-4" /> Salir
          </button>
        ) : (
        <div className="w-full text-white shadow-md"
             style={{ background: 'linear-gradient(120deg,#3C8696,#2C6675)' }}>
          <div className="flex items-center justify-between gap-3 px-4 py-2.5">
            <div className="min-w-0">
              <h1 className="text-base font-extrabold leading-tight truncate">Canal Pilar Domínguez</h1>
              <p className="text-[11px] opacity-90 leading-tight truncate">{t('cabeceraCanal.sub')}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => setActiveSection(null)}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-semibold text-white bg-white/15 border border-white/30 hover:bg-white/25">
                <ArrowLeft className="w-4 h-4" /> {t('cabeceraCanal.volver')}
              </button>
              {/* «¡Opinar!» RETIRADO de esta cabecera (Ann 2026-07-16): solo «Volver».
                  El botón de opinar sigue disponible en el menú de llegada. */}
            </div>
          </div>
        </div>
        )}
        <VisionClienteTab clientMode={true} clienteNombre={user?.nombre} clienteApellido={user?.primer_apellido}
          onMiPerfil={() => setActiveSection('perfil')} onSalir={onLogout} esClub={soloCanal}
          /* Ann 2026-09-26: si se cayó al ir a pagar, la pantalla de «no
             disponible» tiene que ofrecerle TERMINAR, no un callejón. */
          pagoPendiente={user?.pago_pendiente === true || ['cancelada', 'baja', 'impago'].includes(String(user?.membresia_estado || '').toLowerCase())} />
      </div>
    );
  } else if (activeSection === 'perfil') {
    _seccionEl = (
      <MiPerfil
        user={user}
        onLogout={onLogout}
        // La socia solo-Club NUNCA ve el menú: al volver regresa a sus vídeos.
        onBack={() => setActiveSection(soloCanal ? 'canal' : null)}
      />
    );
  } else if (activeSection === 'clinica') {
    // Vista del paciente «Clínica MEFIR» (la nueva; el PatientDashboard legacy queda retirado)
    _seccionEl = (
      <EspacioPacienteClinica
        user={user}
        onBackToSelector={() => setActiveSection(null)}
      />
    );
  }
  if (_seccionEl) {
    return (
      <SeccionBoundary onReset={() => setActiveSection(null)}>
        {_seccionEl}
      </SeccionBoundary>
    );
  }

  // Menú: esperar a que TODOS los datos de layout estén listos → se pinta de una
  // sola vez, sin bloques (tile Clínica, avatar, ticker) apareciendo a saltos.
  if (cargando) {
    return (
      <div className="min-h-screen" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(130% 70% at 50% 0%,#E1F4EF 0%,#EAF6F4 55%)', fontFamily: "'Montserrat', system-ui, sans-serif" }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, color: '#2C6675' }}>
          <div style={{ width: 34, height: 34, border: '3px solid rgba(60,134,150,.25)', borderTopColor: '#3C8696',
            borderRadius: '50%', animation: 'cpd-spin 0.8s linear infinite' }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{t('cargando')}</span>
        </div>
        <style>{`@keyframes cpd-spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const chevron = (
    <svg className="chev" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
  );
  // QUIÉN VE «CLÍNICA» LO DICE EL SERVIDOR, NO UNA MARCA DEL USUARIO.
  // Antes: `tieneFisio || !!user?.activo_clinica`. `activo_clinica` es una marca
  // vieja del documento de la clienta, así que a una clienta Premium (sin espacio
  // clínico) le aparecía el botón «Clínica» (Ann, 21-09-2026: «bloquea el acceso
  // a la opción de clínica YA»). `tieneFisio` viene de /paciente/tipo-acceso, que
  // mira la activación de verdad (plataforma_paciente_activa), la misma que exige
  // el servidor en /paciente/mi-espacio y en los cuestionarios.
  const puedeClinica = tieneFisio;

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(130% 70% at 50% 0%,#E1F4EF 0%,#EAF6F4 55%)', fontFamily: "'Montserrat', system-ui, sans-serif" }}>
      {showDemoBienvenida && (
        <TutorialBienvenida
          steps={[
            { sel: '#tut-fisio', titulo: t('tutorial.fisioT'), texto: t('tutorial.fisioX') },
            { sel: '#tut-clases', titulo: t('tutorial.clasesT'), texto: t('tutorial.clasesX') },
            { sel: '#tut-canal', titulo: t('tutorial.canalT'), texto: t('tutorial.canalX') },
            puedeClinica && { sel: '#tut-clinica', titulo: t('tutorial.clinicaT'), texto: t('tutorial.clinicaX') },
            { sel: '#tut-perfil', titulo: t('tutorial.perfilT'), texto: t('tutorial.perfilX') },
          ].filter(Boolean)}
          onFinish={() => { setShowDemoBienvenida(false); track('tutorial_fin'); track('bienvenida_vista'); try { localStorage.setItem('canal_bienvenida_vista', '1'); } catch (e) { /* noop */ } }}
          onSkip={() => { setShowDemoBienvenida(false); track('tutorial_salto'); try { localStorage.setItem('canal_bienvenida_vista', '1'); } catch (e) { /* noop */ } }}
        />
      )}
      <div className="mi">
        {/* Marca · logos centrados arriba + Opinar justo debajo */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, margin: '4px 0 8px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 16,
            maxWidth: '100%', boxSizing: 'border-box', padding: '8px 4px' }}>
            <img src="/logo-pd.png" alt="Pilar Domínguez" style={{ height: 44, width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block' }} />
            <span style={{ width: 1, height: 38, background: 'rgba(44,107,118,.28)' }} />
            <img src="/logo-mefir-trans.png" alt="Instituto MEFIR" style={{ height: 40, width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block' }} />
          </div>
        </div>

        {/* TICKER · novedades pegado al bloque de logos (solo si hay mensajes) */}
        {novedades && novedades.length > 0 && (
          <div className="ticker" style={{ marginTop: 0 }}>
            <span className="tag"><i />{t('ticker')}</span>
            <div className="track">
              <div className="move">
                {[...novedades, ...novedades].map((m, i) => <span key={i}>{m}</span>)}
              </div>
            </div>
          </div>
        )}

        {/* HEADER · foto + (nombre y comentario a la izquierda, centrados verticalmente) + acciones */}
        <div className="top" style={{ alignItems: 'center' }}>
          <div className="avatar">
            {fotoCliente
              ? <img src={fotoCliente} alt="" />
              : <span className="fb">{(user?.nombre || 'C').charAt(0).toUpperCase()}</span>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="hello">{primerNombre || t('saludo.bienvenida')}</h1>
            {mensajeLlegada && (
              <p className="motto" style={{ marginTop: 6 }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#3C8696" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L4.5 12.5a1 1 0 0 0 .8 1.6H11l-1 7.9 8.5-10.5a1 1 0 0 0-.8-1.6H12z" /></svg>
                <span>{(() => {
                  const nm = primerNombre ? `, ${primerNombre}` : '';
                  // Regreso tras ausencia (backend de veracidad): "te echábamos de menos".
                  if (mensajeLlegada.tipo === 'regreso') {
                    return <><b>{t('saludo.regresoTitulo', { nm })}</b> {mensajeLlegada.tx || t('saludo.regresoTexto')}</>;
                  }
                  // Primera vez (o primera entrada de la sesión, mientras no haya backend): BIENVENIDA en negrita.
                  if (mensajeLlegada.tipo === 'bienvenida' || saludar) {
                    return <><b>{t('saludo.bienvenidaTitulo', { nm })}</b> {mensajeLlegada.tx || t('saludo.bienvenidaTexto')}</>;
                  }
                  // Ya dentro / navegando: frase motivacional.
                  return mensajeLlegada.tx;
                })()}</span>
              </p>
            )}
          </div>
          <div className="actions">
            {/* Píldoras teal (mismo estilo que la barra de navegación). Salir arriba, Opinar debajo. */}
            <button onClick={onLogout}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 13px',
                borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                color: '#2C6675', background: 'rgba(60,134,150,.12)', border: '1px solid rgba(60,134,150,.35)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
              {t('common:comunes.salir', 'Salir')}
            </button>
            <a href={REVIEW_URL} target="_blank" rel="noopener noreferrer" onClick={() => track('opinar_click', 'CanalPilarDashboard')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 13px',
                borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none',
                color: '#2C6675', background: 'rgba(60,134,150,.12)', border: '1px solid rgba(60,134,150,.35)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#2C6675"><path d="M12 2l2.9 6.2 6.6.6-5 4.4 1.5 6.6L12 17l-5.9 2.8 1.5-6.6-5-4.4 6.6-.6z" /></svg>
              {t('cabeceraCanal.opinar')}
            </a>
            {/* Conmutador ES/CA también en el menú (recorrido cliente completo, encargo bilingüe) */}
            <SelectorIdioma sutil />
          </div>
        </div>

        {/* Reclamo de fisioterapia (frente compras-canal-video), debajo de la
            frase motivadora. Handler real (modal de reserva) lo entrega ese
            agente; aquí placeholder temporal para previsualizar. */}
        <div className="fisio-banner" id="tut-fisio">
          <div className="fisio-main">
            <span className="fisio-badge">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff"><path d="M12 2l2.4 6.9L21 9l-5.4 4.3L17.6 21 12 16.9 6.4 21l2-7.7L3 9l6.6-.1z" /></svg>
              {t('fisio.badge')}
            </span>
            <h3 className="fisio-title">{t('fisio.titulo')}</h3>
            <p className="fisio-text"><Trans t={t} i18nKey="fisio.texto" components={{ b: <b /> }} /></p>
            <button type="button" className="fisio-cta" onClick={() => setShowReservaFisio(true)}>
              {t('fisio.cta')}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2c6b76" strokeWidth="2.2"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
            </button>
          </div>
          <div className="fisio-num">
            <span className="fisio-num-big">20<small>%</small></span>
            <span className="fisio-num-lbl">{t('fisio.dto')}</span>
          </div>
        </div>


        {/* Enunciado de sección · justo encima de los menús */}
        <p className="eyebrow" style={{ margin: '26px 4px 12px' }}>Canal Pilar Domínguez · MEFIR</p>

        {/* MENÚ */}
        <div className="menu">
          {/* Cambios Presenciales · SOLO si tiene presencial (Ann 2026-07-20:
              «esto es una aberración, incumple la norma»). Norma: un área solo
              se enseña si la clienta la tiene. «Clínica» ya lo respetaba con
              `puedeClinica`; a esta se le había olvidado, así que una socia
              solo-Club veía «Cambios Presenciales», entraba y no había nada suyo. */}
          {!soloCanal && (
          <button id="tut-clases" className="card" onClick={() => setActiveSection('clases')}>
            <span className="ic">
              <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="17" rx="2.5" /><path d="M3 9.5h18M8 2.5v4M16 2.5v4" /><path d="M15.5 14.5l-4 4-2-2" /></svg>
            </span>
            <div className="txt">
              <div className="title">{t('cards.clasesT')}</div>
              <div className="desc">{t('cards.clasesD')}</div>
            </div>
            {chevron}
          </button>
          )}

          {/* Canal Pilar Domínguez */}
          <button id="tut-canal" className="card" onClick={() => setActiveSection('canal')}>
            <span className="ic">
              <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="4.5" width="19" height="13" rx="2.5" /><path d="M10 9l4.5 2.5L10 14z" fill="currentColor" stroke="none" /><path d="M8 21h8" /></svg>
            </span>
            <div className="txt">
              <div className="title">Canal Pilar Domínguez</div>
              <div className="desc">{t('cards.canalD')}</div>
            </div>
            {chevron}
          </button>

          {/* Clínica (solo si tiene acceso) */}
          {puedeClinica && (
            <button id="tut-clinica" className="card" onClick={() => setActiveSection('clinica')}>
              <span className="ic">
                <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21C7 17 3.5 13.5 3.5 9a4.5 4.5 0 0 1 8.5-2 4.5 4.5 0 0 1 8.5 2c0 4.5-3.5 8-8.5 12z" /><path d="M7.5 12h2l1.5-3 2 5 1-2h2.5" /></svg>
              </span>
              <div className="txt">
                <div className="title">{t('cards.clinicaT')}</div>
                <div className="desc">{t('cards.clinicaD')}</div>
              </div>
              {chevron}
            </button>
          )}

          {/* Mi Perfil */}
          <button id="tut-perfil" className="card" onClick={() => setActiveSection('perfil')}>
            <span className="ic">
              <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.6" /><path d="M5 20c1.2-3.5 4-5 7-5s5.8 1.5 7 5" /></svg>
            </span>
            <div className="txt">
              <div className="title">{t('cards.perfilT')}</div>
              <div className="desc">{t('cards.perfilD')}</div>
            </div>
            {chevron}
          </button>

          {/* Logros · desactivada */}
          <div className="card soon">
            <span className="ic">
              <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l2.9 6.2 6.6.6-5 4.4 1.5 6.6L12 17l-5.9 2.8 1.5-6.6-5-4.4 6.6-.6z" /></svg>
            </span>
            <div className="txt">
              <div className="title">{t('cards.logrosT')}</div>
              <div className="desc">{t('cards.logrosD')}</div>
            </div>
            <span className="badge-soon">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4l2.5 2.5" /></svg>
              {t('cards.enDesarrollo')}
            </span>
          </div>
        </div>

        {/* Horarios de clases presenciales (Ann 2026-07-15 · propuesta B): tablón
            conectado a los horarios REALES (db.horarios_fijos, solo activos). Si el
            endpoint no devuelve nada (fallo o sin horarios) el bloque no se pinta. */}
        {horariosSedes.length > 0 && (
          <section className="hb">
            <div className="hb-head">
              <h2>{t('horarios.titulo')}</h2>
              <span>{t('horarios.sub')}</span>
            </div>
            <div className="hb-panel">
              {horariosSedes.map((s) => (
                <div className="hb-sede" key={s.sede}>
                  <h4>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 21s7-5.5 7-11a7 7 0 10-14 0c0 5.5 7 11 7 11z" /><circle cx="12" cy="10" r="2.6" /></svg>
                    {t(`horarios.sedes.${s.sede}`, { defaultValue: s.sede })}
                  </h4>
                  {s.dias.map((d) => (
                    <div className="hb-dia" key={d.dia}>
                      <span className="hb-d">{t(`horarios.dias.${d.dia}`, { defaultValue: d.dia })}</span>
                      <span className="hb-horas">
                        {d.clases.map((c, i) => (
                          <span key={i} className={`hb-h ${(c.intensidad || '').toLowerCase().includes('suave') ? 'suave' : (c.intensidad || '').toLowerCase().includes('reducid') ? 'reducido' : 'estandar'}`}>
                            {c.hora}
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="hb-leyenda">
              <span className="hb-lg"><i className="hb-dot suave" /> {t('horarios.intensidades.Suave', { defaultValue: 'Suave' })}</span>
              <span className="hb-lg"><i className="hb-dot estandar" /> {t('horarios.intensidades.Estándar', { defaultValue: 'Estándar' })}</span>
              <span className="hb-lg"><i className="hb-dot reducido" /> {t('horarios.intensidades.RecFuncional', { defaultValue: 'Rec. funcional' })}</span>
            </div>
          </section>
        )}

        {/* Ver tutorial: relanza el MISMO tutorial (tour) cuando el cliente quiera. */}
        <button className="cambiar-plataforma" onClick={() => setShowDemoBienvenida(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M9.6 9a2.4 2.4 0 1 1 3.4 2.2c-.7.4-1 .8-1 1.6" /><circle cx="12" cy="16.4" r=".7" fill="currentColor" stroke="none" /></svg>
          {t('verTutorial')}
        </button>

        {onBackToSelector && (
          <button className="cambiar-plataforma" onClick={onBackToSelector}>{t('cambiarPlataforma')}</button>
        )}
      </div>

      {/* FASE 2 · Modal REAL de reserva de fisio (motor de compras-canal-video).
          Consume /api/canal-reservas/* con auth de cliente (token). */}
      {showReservaFisio && (
        <ReservaServicioModal user={user} onClose={() => setShowReservaFisio(false)} />
      )}
    </div>
  );
};

// Blindaje Guardián: TODO el dashboard va envuelto en el límite de error, no solo
// las secciones. Así un fallo en el modal de firma o en el menú (p.ej. primera
// entrada de un Premium) tampoco deja la plataforma en blanco: muestra el aviso
// y permite recargar. El límite interno (secciones) sigue dando una caída más
// suave "volver al menú" cuando el fallo es de una sección concreta.
const CanalPilarDashboard = (props) => (
  <SeccionBoundary onReset={() => { try { localStorage.removeItem('canal_seccion'); } catch (e) { /* noop */ } window.location.reload(); }}>
    {/* Blindaje global del canal: escucha intentos de descarga (clic derecho / Ctrl+S)
        en cualquier reproductor, muestra el aviso legal con el nombre y registra el
        intento para el listado de vigilancia. Ann 2026-07-16. */}
    <BlindajeCanal user={props.user} lang={i18n.language} />
    <CanalPilarDashboardInner {...props} />
  </SeccionBoundary>
);

export default CanalPilarDashboard;
